import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { format } from 'date-fns';
import { NotificationTemplate } from '../../models';
import ConfirmDialog from '../../components/ConfirmDialog';
import LoadingBackdrop from '../../components/LoadingBackdrop';
import { useSnackbar } from 'notistack';
import { useDeleteNotificationTemplateMutation, useGetNotificationTemplatesQuery } from '../../services/connectApi';


const TemplatesList: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { data, isLoading, refetch } = useGetNotificationTemplatesQuery({ page: page + 1, page_size: pageSize });
  const templates = data?.results || [];
  React.useEffect(() => {
    if (data?.count !== undefined) setTotalCount(data.count);
  }, [data]);

  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteNotificationTemplateMutation();

  const handleEdit = (id: string) => {
    navigate(`/connect/templates/edit/${id}`);
  };

  const handleView = (id: string) => {
    navigate(`/connect/templates/view/${id}`);
  };

  const handleDuplicate = (template: NotificationTemplate) => {
    const duplicatedTemplate: Partial<NotificationTemplate> = {
      ...template,
      name: `${template.name} (Copy)`,
    };
    delete duplicatedTemplate.id;
    delete duplicatedTemplate.created_at;
    delete duplicatedTemplate.updated_at;

    navigate('/connect/templates/new', { state: { template: duplicatedTemplate } });
  };

  const handleDeleteClick = (template: NotificationTemplate) => {
    setSelectedTemplate(template);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedTemplate) {
      try {
        await deleteTemplate(selectedTemplate.id).unwrap();
        enqueueSnackbar('Template deleted successfully', { variant: 'success' });
        refetch();
      } catch (error) {
        enqueueSnackbar('Failed to delete template', { variant: 'error' });
      }
    }
    setDeleteConfirmOpen(false);
  };

  const getChannelTypes = (template: NotificationTemplate): string[] => {
    const types: string[] = [];
    if (template.push_title && template.push_body) types.push('Push');
    if (template.email_subject && (template.email_body || template.email_html_body)) types.push('Email');
    if (template.sms_body) types.push('SMS');
    if (template.whatsapp_body || template.whatsapp_template_name) types.push('WhatsApp');
    return types;
  };

  const columns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: 'Name', 
      flex: 1,
      minWidth: 200
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      flex: 1,
      minWidth: 250
    },
    {
      field: 'channels',
      headerName: 'Channels',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<NotificationTemplate, any>) => (
        <Box>
          {getChannelTypes(params.row).map((type) => (
            <Chip 
              key={type} 
              label={type} 
              size="small" 
              color="primary" 
              variant="outlined" 
              sx={{ 
                mr: 0.5,
                // Ensure chip doesn't get cropped in DataGrid cell
                maxHeight: '24px',
                '& .MuiChip-label': {
                  padding: '0 8px',
                }
              }} 
            />
          ))}
        </Box>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      minWidth: 180,
      valueFormatter: (params: { value: string | null }) => {
        return params.value ? format(new Date(params.value), 'MMM d, yyyy HH:mm') : '-';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<NotificationTemplate, any>) => (
        <Box>
          <Tooltip title="View">
            <IconButton onClick={() => handleView(params.row.id)} size="small">
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton onClick={() => handleEdit(params.row.id)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton onClick={() => handleDuplicate(params.row)} size="small">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteClick(params.row)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return <Typography>Loading templates...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Notification Templates
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Create and manage templates for different notification channels (push, email, SMS, WhatsApp)
      </Typography>
      
      <Paper elevation={2} sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={templates}
          columns={columns}
          pageSizeOptions={[10, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          loading={isLoading}
          rowCount={totalCount}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            sorting: {
              sortModel: [{ field: 'updated_at', sort: 'desc' }],
            },
          }}
        />
      </Paper>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Template"
        content={`Are you sure you want to delete the template "${selectedTemplate?.name}"? This action cannot be undone.`}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <LoadingBackdrop open={isDeleting} />
    </Box>
  );
};

export default TemplatesList;
